import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../config/api';
import './CarDetails.css';

//  Importă iconițele
import { BsCalendar3 } from 'react-icons/bs';
import { GiGearStickPattern } from 'react-icons/gi';
import { MdLocalGasStation } from 'react-icons/md';
import { FaTachometerAlt, FaCar } from 'react-icons/fa';
import { TbEngine } from 'react-icons/tb';
import { AiFillThunderbolt } from 'react-icons/ai';
import { IoColorPaletteOutline } from 'react-icons/io5';

const CarDetails = () => {
  const [formData, setFormData] = useState({
    nume: '',
    prenume: '',
    telefon: '',
    angajat: '',
    datorii: '',
    alteRate: '',
    venitLunar: ''
  });

  const [isSubmittingCredit, setIsSubmittingCredit] = useState(false);
  
  // 🆕 STATE pentru popup succes
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showContactModal, setShowContactModal] = useState(false);
  
  // STATE-uri pentru ZOOM
  const [showZoomModal, setShowZoomModal] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialPinchDistance, setInitialPinchDistance] = useState(null);
  const zoomImageRef = useRef(null);
  const zoomContainerRef = useRef(null);
  
  const [contactForm, setContactForm] = useState({
    nume: '',
    telefon: '',
    email: '',
    mesaj: ''
  });

  useEffect(() => {
    if (!id || id === 'undefined') {
      navigate('/masini');
      return;
    }
    fetchCarDetails();
  }, [id]);

  // Disable body scroll when modals are open
  useEffect(() => {
    if (showZoomModal || showSuccessPopup) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showZoomModal, showSuccessPopup]);

  const fetchCarDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/cars/${id}`);
      
      if (!response.ok) {
        throw new Error('Mașina nu a fost găsită');
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        setCar(data.data);
      } else {
        throw new Error('Răspuns invalid de la server');
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Nu am putut încărca detaliile mașinii');
      navigate('/masini');
    } finally {
      setLoading(false);
    }
  };

  const getCarImages = () => {
    if (!car) return [];
    const images = car.imagini || [];
    if (images.length === 0 && car.imagineaPrincipala) {
      return [car.imagineaPrincipala.startsWith('http') ? 
              car.imagineaPrincipala : 
              `${API_BASE_URL}${car.imagineaPrincipala}`];
    }
    if (images.length === 0) return ['https://via.placeholder.com/800x600?text=No+Image'];
    return images.map(img => {
      if (img.startsWith('http')) return img;
      if (img.startsWith('/')) return `${API_BASE_URL}${img}`;
      return `${API_BASE_URL}/${img}`;
    });
  };

  const formatPrice = (price) => price ? new Intl.NumberFormat('de-DE').format(price) : 'N/A';
  const formatKilometraj = (km) => km ? new Intl.NumberFormat('de-DE').format(km) : 'N/A';

  // FUNCȚII PENTRU ZOOM
  const openZoomModal = () => {
    setShowZoomModal(true);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const closeZoomModal = () => {
    setShowZoomModal(false);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 4));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => {
      const newZoom = Math.max(prev - 0.5, 1);
      if (newZoom === 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleDoubleClick = (e) => {
    e.preventDefault();
    if (zoomLevel > 1) {
      handleResetZoom();
    } else {
      setZoomLevel(2.5);
    }
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.2 : 0.2;
    setZoomLevel(prev => {
      const newZoom = Math.max(1, Math.min(prev + delta, 4));
      if (newZoom === 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  };

  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      setPosition({ x: newX, y: newY });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const distance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      setInitialPinchDistance(distance);
    } else if (e.touches.length === 1 && zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ 
        x: e.touches[0].clientX - position.x, 
        y: e.touches[0].clientY - position.y 
      });
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && initialPinchDistance) {
      e.preventDefault();
      const currentDistance = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      const scale = currentDistance / initialPinchDistance;
      setZoomLevel(prev => {
        const newZoom = Math.max(1, Math.min(prev * scale, 4));
        if (newZoom === 1) {
          setPosition({ x: 0, y: 0 });
        }
        return newZoom;
      });
      setInitialPinchDistance(currentDistance);
    } else if (e.touches.length === 1 && isDragging && zoomLevel > 1) {
      const newX = e.touches[0].clientX - dragStart.x;
      const newY = e.touches[0].clientY - dragStart.y;
      setPosition({ x: newX, y: newY });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setInitialPinchDistance(null);
  };

  const handleZoomPrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
    handleResetZoom();
  };

  const handleZoomNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    handleResetZoom();
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showSuccessPopup && e.key === 'Escape') {
        setShowSuccessPopup(false);
        return;
      }
      if (!showZoomModal) return;
      
      switch (e.key) {
        case 'Escape':
          closeZoomModal();
          break;
        case 'ArrowLeft':
          setCurrentImageIndex(prev => prev === 0 ? (car?.imagini?.length || 1) - 1 : prev - 1);
          handleResetZoom();
          break;
        case 'ArrowRight':
          setCurrentImageIndex(prev => prev === (car?.imagini?.length || 1) - 1 ? 0 : prev + 1);
          handleResetZoom();
          break;
        case '+':
        case '=':
          handleZoomIn();
          break;
        case '-':
          handleZoomOut();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showZoomModal, showSuccessPopup, car]);

  const handleContactSubmit = (e) => {
    e.preventDefault();
    
    const message = `
*Cerere informații - ${car.marca} ${car.model}*

*Mașină:* ${car.marca} ${car.model}
*Preț:* €${formatPrice(car.pret)}
*Link:* ${window.location.href}

---

*Contact:*
*Nume:* ${contactForm.nume}
*Telefon:* ${contactForm.telefon}
*Email:* ${contactForm.email}
*Mesaj:* ${contactForm.mesaj}
    `.trim();

    const encodedMessage = encodeURIComponent(message);
    const whatsappURL = `https://wa.me/40770905087?text=${encodedMessage}`;
    
    window.open(whatsappURL, '_blank');
    setShowContactModal(false);
    setContactForm({ nume: '', telefon: '', email: '', mesaj: '' });
  };

  const handleWhatsApp = () => {
    const message = `Bună ziua! Sunt interesat de ${car.marca} ${car.model} (${car.anFabricatie}) - €${formatPrice(car.pret)}. Link: ${window.location.href}`;
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/40770905087?text=${encodedMessage}`, '_blank');
  };

  // 🆕 FUNCȚIE PENTRU FORMULAR CREDIT CU POPUP SUCCES
  const handleCreditFormSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.nume ||
      !formData.prenume ||
      !formData.telefon ||
      !formData.angajat ||
      !formData.datorii ||
      !formData.alteRate ||
      !formData.venitLunar
    ) {
      alert('Vă rugăm să completați toate câmpurile!');
      return;
    }

    setIsSubmittingCredit(true);

    try {
      // 1️⃣ Trimite către Airtable prin backend
      const airtableResponse = await fetch(`${API_BASE_URL}/api/airtable/submit-credit-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          carInfo: `${car.marca} ${car.model} (${car.anFabricatie}) - €${formatPrice(car.pret)}`
        })
      });

      const airtableData = await airtableResponse.json();

      if (!airtableData.success) {
        throw new Error(airtableData.message || 'Eroare la salvarea datelor');
      }

      console.log('✅ Salvat în Airtable cu succes!');

      // 2️⃣ Deschide WhatsApp
      const message = `
*Formular Credit Mașină - ${car.marca} ${car.model} (${car.anFabricatie})*

*Nume:* ${formData.nume}
*Prenume:* ${formData.prenume}
*Telefon:* ${formData.telefon}

*Sunteți angajat / pensionar de minim 3 luni?* ${formData.angajat}
*Aveți datorii neachitate / restanțe la alte bănci?* ${formData.datorii}

*Alte rate lunare:* ${formData.alteRate} lei
*Venit lunar net:* ${formData.venitLunar} lei

Link mașină: ${window.location.href}
      `.trim();

      const encodedMessage = encodeURIComponent(message);
      const whatsappURL = `https://wa.me/40770905087?text=${encodedMessage}`;
      window.open(whatsappURL, '_blank');

      // 3️⃣ Resetează formularul
      setFormData({
        nume: '',
        prenume: '',
        telefon: '',
        angajat: '',
        datorii: '',
        alteRate: '',
        venitLunar: ''
      });

      // 4️⃣ 🆕 Arată popup-ul de succes
      setShowSuccessPopup(true);

    } catch (error) {
      console.error('❌ Error:', error);
      alert('A apărut o eroare la trimiterea formularului. Vă rugăm să încercați din nou.');
    } finally {
      setIsSubmittingCredit(false);
    }
  };

  if (loading) {
    return (
      <div>
        <div className="car-details-loading">
          <div className="spinner"></div>
          <p>Se încarcă...</p>
        </div>
      </div>
    );
  }

  if (!car) {
    return (
      <div>
        <div className="car-details-error">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <h3>Mașina nu a fost găsită</h3>
          <p>Ne pare rău, dar mașina pe care o căutați nu mai este disponibilă.</p>
          <button className="back-btn" onClick={() => navigate('/masini')}>
            Înapoi la listă
          </button>
        </div>
      </div>
    );
  }

  const images = getCarImages();

  return (
    <div>

      <main className="car-details-page">
        <div className="car-details-container">
          <button className="back-link" onClick={() => navigate('/masini')}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke="currentColor" strokeWidth="2" 
                    strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Înapoi la listă
          </button>

          <div className="car-details-content">
            {/* LEFT SIDE - GALLERY */}
            <div className="car-details-gallery">
              <div className="main-image-container">
                <img
                  src={images[currentImageIndex]}
                  alt={`${car.marca} ${car.model}`}
                  className="main-image zoomable-image"
                  onClick={openZoomModal}
                  onError={(e) => (e.target.src = 'https://via.placeholder.com/800x600?text=No+Image')}
                />
                
                <div className="zoom-hint">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                    <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M11 8v6M8 11h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <span>Click pentru zoom</span>
                </div>
                
                {images.length > 1 && (
                  <>
                    <button 
                      className="gallery-nav-btn prev"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex(prev => 
                          prev === 0 ? images.length - 1 : prev - 1
                        );
                      }}
                    >
                      ‹
                    </button>
                    <button 
                      className="gallery-nav-btn next"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCurrentImageIndex(prev => 
                          prev === images.length - 1 ? 0 : prev + 1
                        );
                      }}
                    >
                      ›
                    </button>
                    <div className="image-counter">
                      {currentImageIndex + 1} / {images.length}
                    </div>
                  </>
                )}
              </div>

              {images.length > 1 && (
                <div className="thumbnail-container">
                  {images.map((img, index) => (
                    <img
                      key={index}
                      src={img}
                      alt={`${car.marca} ${car.model} ${index + 1}`}
                      className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                      onClick={() => setCurrentImageIndex(index)}
                      onError={(e) => (e.target.src = 'https://via.placeholder.com/150x100?text=No+Image')}
                    />
                  ))}
                </div>
              )}

              {/* DESCRIPTION SECTION */}
              {car.descriere && (
                <div className="car-description">
                  <h3>Descriere</h3>
                  <p>{car.descriere}</p>
                </div>
              )}

              {/* FORMULAR CREDIT */}
              <div className="credit-form-container">
                <h3>Solicită un Credit</h3>
                <p>Completează formularul și te vom contacta în cel mai scurt timp</p>

                <form className="credit-form" onSubmit={handleCreditFormSubmit}>
                  <div className="form-row">
                    <input
                      type="text"
                      name="nume"
                      placeholder="Nume *"
                      value={formData.nume}
                      onChange={(e) => setFormData({ ...formData, nume: e.target.value })}
                      required
                      disabled={isSubmittingCredit}
                    />
                    <input
                      type="text"
                      name="prenume"
                      placeholder="Prenume *"
                      value={formData.prenume}
                      onChange={(e) => setFormData({ ...formData, prenume: e.target.value })}
                      required
                      disabled={isSubmittingCredit}
                    />
                  </div>

                  <input
                    type="tel"
                    name="telefon"
                    placeholder="Număr de telefon *"
                    value={formData.telefon}
                    onChange={(e) => setFormData({ ...formData, telefon: e.target.value })}
                    required
                    disabled={isSubmittingCredit}
                  />

                  <div className="radio-group">
                    <label>Angajat/pensionar minim 3 luni *</label>
                    <div style={{flexDirection:'column', display:'flex'}}>
                      <label>
                        <input
                          type="radio"
                          name="angajat"
                          value="Da"
                          checked={formData.angajat === 'Da'}
                          onChange={(e) => setFormData({ ...formData, angajat: e.target.value })}
                          disabled={isSubmittingCredit}
                        /> Da
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="angajat"
                          value="Nu"
                          checked={formData.angajat === 'Nu'}
                          onChange={(e) => setFormData({ ...formData, angajat: e.target.value })}
                          disabled={isSubmittingCredit}
                        /> Nu
                      </label>
                    </div>
                  </div>

                  <div className="radio-group">
                    <label>Aveți datorii neachitate / restanțe? *</label>
                    <div style={{flexDirection:'column', display:'flex'}}>
                      <label>
                        <input
                          type="radio"
                          name="datorii"
                          value="Da"
                          checked={formData.datorii === 'Da'}
                          onChange={(e) => setFormData({ ...formData, datorii: e.target.value })}
                          disabled={isSubmittingCredit}
                        /> Da
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="datorii"
                          value="Nu"
                          checked={formData.datorii === 'Nu'}
                          onChange={(e) => setFormData({ ...formData, datorii: e.target.value })}
                          disabled={isSubmittingCredit}
                        /> Nu
                      </label>
                    </div>
                  </div>

                  <input
                    type="number"
                    name="alteRate"
                    placeholder="Suma altor rate lunare (lei) *"
                    value={formData.alteRate}
                    onChange={(e) => setFormData({ ...formData, alteRate: e.target.value })}
                    required
                    disabled={isSubmittingCredit}
                  />

                  <input
                    type="number"
                    name="venitLunar"
                    placeholder="Venit lunar net (lei) *"
                    value={formData.venitLunar}
                    onChange={(e) => setFormData({ ...formData, venitLunar: e.target.value })}
                    required
                    disabled={isSubmittingCredit}
                  />

                  <button type="submit" className="submit-btn" disabled={isSubmittingCredit}>
                    {isSubmittingCredit ? '⏳ Se trimite...' : 'Trimite Formular'}
                  </button>
                </form>
              </div>


              {/* EQUIPMENT SECTION */}
              {car.dotari && car.dotari.length > 0 && (
                <div className="car-equipment">
                  <h3>Dotări (una pe fiecare linie)</h3>
                  <ul className="equipment-list">
                    {car.dotari.map((dotare, index) => (
                      <li key={index}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" 
                                strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {dotare}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* RIGHT SIDE - INFO */}
            <div className="car-details-info">
              {/* HEADER */}
              <div className="car-details-header">
                <div>
                  {car._id && (
                    <p className="car-id">ID:{car._id.slice(-6).toUpperCase()}</p>
                  )}
                  <h1>{car.marca} {car.model}</h1>
                  <p className="car-subtitle">{car.anFabricatie}</p>
                  {car.numarInmatriculare && (
                    <p className="car-subtitle">Număr de locuri: {car.numarInmatriculare}</p>
                  )}
                  {car.locatie && (
                    <p className="car-subtitle">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{display: 'inline', marginRight: '4px'}}>
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      {car.locatie}
                    </p>
                  )}
                </div>

                <div className="car-price-tag">
                  <span className="price">€ {formatPrice(car.pret)}</span>
                  <span className="currency">EUR</span>
                </div>
              </div>

              {/* MAIN SPECS CU ICONIȚE */}
              <div className="main-specs-grid-icons">
                <div className="spec-icon-item">
                  <BsCalendar3 className="spec-icon" />
                  <div className="spec-icon-text">
                    <span className="spec-icon-label">An fabricație</span>
                    <span className="spec-icon-value">{car.anFabricatie}</span>
                  </div>
                </div>

                <div className="spec-icon-item">
                  <TbEngine className="spec-icon" />
                  <div className="spec-icon-text">
                    <span className="spec-icon-label">Capacitate motor</span>
                    <span className="spec-icon-value">{car.capacitateCilindrica} cc</span>
                  </div>
                </div>

                <div className="spec-icon-item">
                  <MdLocalGasStation className="spec-icon" />
                  <div className="spec-icon-text">
                    <span className="spec-icon-label">Combustibil</span>
                    <span className="spec-icon-value">{car.carburant}</span>
                  </div>
                </div>

                <div className="spec-icon-item">
                  <GiGearStickPattern className="spec-icon" />
                  <div className="spec-icon-text">
                    <span className="spec-icon-label">Transmisie</span>
                    <span className="spec-icon-value">{car.transmisie}</span>
                  </div>
                </div>

                <div className="spec-icon-item">
                  <FaCar className="spec-icon" />
                  <div className="spec-icon-text">
                    <span className="spec-icon-label">Caroserie</span>
                    <span className="spec-icon-value">{car.caroserie}</span>
                  </div>
                </div>

                <div className="spec-icon-item">
                  <FaTachometerAlt className="spec-icon" />
                  <div className="spec-icon-text">
                    <span className="spec-icon-label">Kilometraj</span>
                    <span className="spec-icon-value">{formatKilometraj(car.kilometraj)} km</span>
                  </div>
                </div>

                <div className="spec-icon-item">
                  <AiFillThunderbolt className="spec-icon" />
                  <div className="spec-icon-text">
                    <span className="spec-icon-label">Putere</span>
                    <span className="spec-icon-value">{car.putere} CP</span>
                  </div>
                </div>

                {car.culoare && (
                  <div className="spec-icon-item">
                    <IoColorPaletteOutline className="spec-icon" />
                    <div className="spec-icon-text">
                      <span className="spec-icon-label">Culoare</span>
                      <span className="spec-icon-value">{car.culoare}</span>
                    </div>
                  </div>
                )}
              </div>


              {/* ADDITIONAL INFO */}
              <div className="additional-info">
                <h3>Informații suplimentare</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Combustibil</span>
                    <span className="info-value">{car.carburant}</span>
                  </div>
                  {car.tractiune && (
                    <div className="info-item">
                      <span className="info-label">Tracțiune</span>
                      <span className="info-value">{car.tractiune}</span>
                    </div>
                  )}
                  {car.serieSasiu && (
                    <div className="info-item">
                      <span className="info-label">Serie Șasiu</span>
                      <span className="info-value">{car.serieSasiu}</span>
                    </div>
                  )}
                  {car.nrProprietari && (
                    <div className="info-item">
                      <span className="info-label">Număr proprietari</span>
                      <span className="info-value">{car.nrProprietari}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* STATUS BADGES */}
              <div className="car-status-badges">
                {car.disponibilitate && (
                  <span className={`status-badge ${car.disponibilitate === 'Avem pe stoc' ? 'in-stock' : 'out-stock'}`}>
                    {car.disponibilitate}
                  </span>
                )}
                {car.garantieInclusa && (
                  <span className="status-badge green">Garanție inclusă</span>
                )}
                {car.tvaDeductibil && (
                  <span className="status-badge">TVA Nedeductibil</span>
                )}
              </div>

              {/* CONTACT ACTIONS */}
              <div className="contact-actions">
                <button className="contact-btn whatsapp" onClick={handleWhatsApp}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" 
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  WhatsApp
                </button>
                <button className="contact-btn secondary" onClick={() => setShowContactModal(true)}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" 
                          stroke="currentColor" strokeWidth="2"/>
                    <path d="M22 6l-10 7L2 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Solicită informații
                </button>
                <a style={{backgroundColor:'red'}} href={`tel:+40770905087`} className="contact-btn secondary">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" 
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Sună acum
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* 🆕 SUCCESS POPUP MODAL */}
        {showSuccessPopup && (
          <div className="success-popup-overlay" onClick={() => setShowSuccessPopup(false)}>
            <div className="success-popup-modal" onClick={(e) => e.stopPropagation()}>
              {/* Close button */}
              <button className="success-popup-close" onClick={() => setShowSuccessPopup(false)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" 
                        strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              
              {/* Success icon */}
              <div className="success-popup-icon">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="2"/>
                  <path d="M8 12l3 3 5-6" stroke="#ef4444" strokeWidth="2" 
                        strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              
              {/* Title */}
              <h2 className="success-popup-title">Vă mulțumim!</h2>
              
              {/* Message */}
              <p className="success-popup-message">
                Vă mulțumim pentru completarea formularului, vă vom contacta cât mai curând.
              </p>
              
              {/* Button */}
              <button className="success-popup-btn" onClick={() => setShowSuccessPopup(false)}>
                Închide
              </button>
            </div>
          </div>
        )}

        {/* ZOOM MODAL */}
        {showZoomModal && (
          <div 
            className="zoom-modal-overlay"
            onClick={closeZoomModal}
          >
            <div 
              className="zoom-modal-content"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="zoom-close-btn" onClick={closeZoomModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" 
                        strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              <div className="zoom-controls">
                <button onClick={handleZoomOut} disabled={zoomLevel <= 1} title="Zoom out (-)">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                    <path d="M21 21l-4.35-4.35M8 11h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
                <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
                <button onClick={handleZoomIn} disabled={zoomLevel >= 4} title="Zoom in (+)">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/>
                    <path d="M21 21l-4.35-4.35M11 8v6M8 11h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
                <button onClick={handleResetZoom} title="Reset zoom">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M3 12a9 9 0 109-9 9.75 9.75 0 00-6.74 2.74L3 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 3v5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>

              <div 
                className="zoom-image-container"
                ref={zoomContainerRef}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onDoubleClick={handleDoubleClick}
                style={{ cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in' }}
              >
                <img
                  ref={zoomImageRef}
                  src={images[currentImageIndex]}
                  alt={`${car.marca} ${car.model}`}
                  className="zoom-image"
                  style={{
                    transform: `translate(${position.x}px, ${position.y}px) scale(${zoomLevel})`,
                    transition: isDragging ? 'none' : 'transform 0.2s ease-out'
                  }}
                  draggable={false}
                  onError={(e) => (e.target.src = 'https://via.placeholder.com/800x600?text=No+Image')}
                />
              </div>

              {images.length > 1 && (
                <>
                  <button className="zoom-nav-btn prev" onClick={handleZoomPrevImage}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                      <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" 
                            strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  <button className="zoom-nav-btn next" onClick={handleZoomNextImage}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" 
                            strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </>
              )}

              <div className="zoom-image-counter">
                {currentImageIndex + 1} / {images.length}
              </div>

              <div className="zoom-instructions">
                <span>🖱️ Scroll pentru zoom</span>
                <span>👆 Double-click pentru zoom rapid</span>
                <span>✋ Trage pentru a naviga</span>
              </div>
            </div>
          </div>
        )}

        {/* CONTACT MODAL */}
        {showContactModal && (
          <div className="contact-modal-overlay" onClick={() => setShowContactModal(false)}>
            <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowContactModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" 
                        strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              <h3>Solicită informații</h3>
              <p>Completează formularul și te vom contacta în cel mai scurt timp.</p>
              <form onSubmit={handleContactSubmit}>
                <input
                  type="text"
                  placeholder="Nume complet *"
                  value={contactForm.nume}
                  onChange={(e) => setContactForm({...contactForm, nume: e.target.value})}
                  required
                />
                <input
                  type="tel"
                  placeholder="Telefon *"
                  value={contactForm.telefon}
                  onChange={(e) => setContactForm({...contactForm, telefon: e.target.value})}
                  required
                />
                <input
                  type="email"
                  placeholder="Email *"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                  required
                />
                <textarea
                  placeholder="Mesajul tău"
                  value={contactForm.mesaj}
                  onChange={(e) => setContactForm({...contactForm, mesaj: e.target.value})}
                  rows="4"
                />
                <button type="submit" className="submit-btn">
                  Trimite cerere
                </button>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CarDetails;